/**
 * ListViewSchema for Sales Order DocType
 *
 * Schema-driven configuration for Sales Order list page.
 * Enhanced with storefront-specific columns (payment method, source channel)
 * and quick filters for order lifecycle states.
 *
 * All list layout changes should be made in this file.
 *
 * Custom Fields required (created by zaviago_backend patches):
 * - source_channel: add_source_channel_custom_field.py
 * - payment_method_type: add_checkout_custom_fields.py
 */

import type { ListViewSchema } from "./types";

export const salesOrderListViewSchema: ListViewSchema = {
  doctype: "Sales Order",
  views: {
    experience: {
      columns: [
        {
          fieldname: "name",
          label: "Order",
          width: 160,
          align: "left",
          type: "Link",
          sortable: true,
        },
        {
          fieldname: "customer_name",
          label: "Customer",
          width: "grow",
          align: "left",
          type: "Data",
          sortable: true,
        },
        {
          fieldname: "status",
          label: "Status",
          width: 150,
          align: "center",
          type: "OrderStatus",
          sortable: false,
        },
        {
          fieldname: "grand_total",
          label: "Total",
          width: 130,
          align: "right",
          type: "Currency",
          sortable: true,
        },
        {
          fieldname: "payment_method_type",
          label: "Payment",
          width: 110,
          align: "center",
          type: "Tag",
          sortable: true,
          hideOnMobile: true,
        },
        {
          fieldname: "source_channel",
          label: "Channel",
          width: 100,
          align: "center",
          type: "Channel",
          sortable: true,
          hideOnMobile: true,
        },
        {
          fieldname: "creation",
          label: "Date",
          width: 140,
          align: "right",
          type: "RelativeTime",
          sortable: true,
        },
      ],
      defaultSort: {
        fieldname: "creation",
        direction: "desc",
      },
      defaultFilters: [],
      quickFilters: [
        {
          id: "all",
          label: "All",
          filters: [],
          default: true,
        },
        {
          id: "storefront",
          label: "Storefront",
          filters: [["source_channel", "=", "Storefront"]],
        },
        {
          id: "lazada",
          label: "Lazada",
          filters: [["source_channel", "=", "Lazada"]],
        },
        {
          id: "needs_payment",
          label: "Needs Payment",
          filters: [
            ["source_channel", "=", "Storefront"],
            ["status", "=", "To Deliver and Bill"],
            ["payment_method_type", "!=", "COD"],
          ],
        },
        {
          id: "ready_to_ship",
          label: "Ready to Ship",
          filters: [
            ["source_channel", "=", "Storefront"],
            ["status", "=", "To Deliver"],
          ],
        },
        {
          id: "shipped",
          label: "Shipped",
          filters: [
            ["source_channel", "=", "Storefront"],
            ["status", "=", "To Bill"],
          ],
        },
        {
          id: "completed",
          label: "Completed",
          filters: [
            ["source_channel", "=", "Storefront"],
            ["status", "=", "Completed"],
          ],
        },
      ],
      additionalFields: ["per_delivered", "per_billed", "docstatus", "advance_paid", "shopee_order_status", "lazada_order_status", "total_qty", "customer", "contact_display", "contact_mobile", "contact_phone", "shipping_address_name", "address_display", "custom_storefront_source"],
      pageSizeOptions: [20, 50, 100, 200, 400, 500],
      // Multi-store: append one tab per storefront when the tenant has 2+ stores.
      storefrontFilterField: "custom_storefront_source",
    },
  },
};
